(function () {
  'use strict';

  const EDIT_LINK_SELECTOR = '.mw-apo-configure-link';
  const PDP_CTA_SELECTOR = '.mw-apo-add-to-cart-custom, .mw-apo-add-to-cart-cloned, .mw-apo-add-to-cart-proxy';
  const EDIT_SESSION_STORAGE_KEY = 'mwApoEditSession';
  const EDIT_QUERY_PARAM = 'edit_cart_id';
  const EDIT_VARIANT_PARAM = 'edit_variant_id';
  const UPDATE_LABEL = 'Update';
  const EDIT_ACTIVATION_MAX_AGE_MS = 5 * 60 * 1000;

  function readJsonDataAttribute(element, attributeName) {
    const rawValue = element?.getAttribute(attributeName);
    if (!rawValue) return null;

    try {
      return JSON.parse(rawValue);
    } catch (error) {
      console.error('[MW APO Edit] Failed to parse line item JSON payload', error);
      return null;
    }
  }

  function getClosestProductUrl(editLink) {
    const lineItem = editLink.closest('line-item');
    const productLink = lineItem?.querySelector(`a[href*="/products/"]:not(${EDIT_LINK_SELECTOR})`);
    if (productLink?.getAttribute('href')) {
      return productLink.getAttribute('href');
    }

    return lineItem?.dataset.lineItemUrl || '';
  }

  function persistEditSession(session) {
    if (!session) return;

    try {
      window.sessionStorage.setItem(EDIT_SESSION_STORAGE_KEY, JSON.stringify(session));
    } catch (error) {
      console.error('[MW APO Edit] Failed to persist edit session', error);
    }
  }

  function readStoredEditSession() {
    try {
      const rawValue = window.sessionStorage.getItem(EDIT_SESSION_STORAGE_KEY);
      if (!rawValue) return null;

      const parsed = JSON.parse(rawValue);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      console.error('[MW APO Edit] Failed to read stored edit session', error);
      return null;
    }
  }

  function clearStoredEditSession() {
    try {
      window.sessionStorage.removeItem(EDIT_SESSION_STORAGE_KEY);
    } catch (error) {
      console.error('[MW APO Edit] Failed to clear edit session', error);
    }
  }

  function buildEditUrl(editLink) {
    const productUrl = getClosestProductUrl(editLink);
    if (!productUrl) return '';

    const url = new URL(productUrl, window.location.origin);
    const lineItem = editLink.closest('line-item');
    const cartId = lineItem?.dataset.lineItemCartId || editLink.dataset.mwApoCartId || '';
    const variantId = editLink.dataset.mwApoVariantId || '';

    if (variantId && !url.searchParams.has('variant')) {
      url.searchParams.set('variant', variantId);
    }

    if (cartId) {
      url.searchParams.set(EDIT_QUERY_PARAM, cartId);
    }

    if (variantId) {
      url.searchParams.set(EDIT_VARIANT_PARAM, variantId);
    }

    return url.toString();
  }

  function redirectToEditPage(editLink) {
    const nextUrl = buildEditUrl(editLink);
    if (!nextUrl) return;
    const lineItem = editLink.closest('line-item');
    const selectedProperties = readJsonDataAttribute(lineItem, 'data-line-item-properties');
    const targetUrl = new URL(nextUrl, window.location.origin);
    persistEditSession({
      cartId: lineItem?.dataset.lineItemCartId || editLink.dataset.mwApoCartId || '',
      variantId: editLink.dataset.mwApoVariantId || '',
      targetPath: targetUrl.pathname,
      pendingActivation: true,
      createdAt: Date.now(),
      selectedProperties: Array.isArray(selectedProperties) ? selectedProperties : [],
    });
    window.location.href = nextUrl;
  }

  function handleEditLinkClick(event) {
    const editLink = event.target.closest(EDIT_LINK_SELECTOR);
    if (!editLink) return;

    const nextUrl = buildEditUrl(editLink);
    if (!nextUrl) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    redirectToEditPage(editLink);
  }

  function getEditSessionFromUrl() {
    const url = new URL(window.location.href);
    const storedSession = readStoredEditSession();
    const cartId = url.searchParams.get(EDIT_QUERY_PARAM);

    if (
      !cartId &&
      storedSession?.pendingActivation &&
      storedSession.targetPath === window.location.pathname &&
      Date.now() - Number(storedSession.createdAt || 0) < EDIT_ACTIVATION_MAX_AGE_MS
    ) {
      return {
        ...storedSession,
        pendingActivation: false,
        targetPath: window.location.pathname,
      };
    }

    if (cartId) {
      return {
        ...(storedSession && storedSession.cartId === cartId ? storedSession : {}),
        cartId,
        variantId: url.searchParams.get(EDIT_VARIANT_PARAM) || '',
        pendingActivation: false,
        targetPath: window.location.pathname,
      };
    }

    return null;
  }

  function clearEditSessionOnReturn() {
    const url = new URL(window.location.href);
    if (url.searchParams.has(EDIT_QUERY_PARAM)) return;
    if (window.themeVariables?.settings?.pageType !== 'cart') return;
    clearStoredEditSession();
  }

  function applyUpdateLabels(root) {
    root.querySelectorAll(PDP_CTA_SELECTOR).forEach((button) => {
      if (!button) return;
      button.textContent = UPDATE_LABEL;
      button.dataset.mwApoEditLabelApplied = 'true';
    });
  }

  function startProductEditMode(editSession) {
    if (!editSession) return;

    persistEditSession({
      ...editSession,
      isEditing: true,
    });

    window.mwApoEditSession = {
      ...editSession,
      isEditing: true,
    };

    document.documentElement.classList.add('mw-apo-edit-mode');
    document.documentElement.dataset.mwApoEditCartId = editSession.cartId;
    applyUpdateLabels(document);
    window.dispatchEvent(new CustomEvent('mw-apo:edit-mode-requested', { detail: { editSession } }));
  }

  document.addEventListener('click', handleEditLinkClick, true);
  clearEditSessionOnReturn();

  if (window.themeVariables?.settings?.pageType === 'product') {
    startProductEditMode(getEditSessionFromUrl());
  }

  window.mwApoRedirectToEditPage = redirectToEditPage;
  window.mwApoClearEditSession = clearStoredEditSession;
})();
